import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import API from '../api/api';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useI18n } from '../contexts/I18nContext';

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (value) => Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
const getItemId = (item) => Number(item?.product_id || item?.tire_id || item?.id);

const buildReference = (name) => {
  const base = String(name || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 12) || 'ARTICLE';
  return `${base}-${String(Date.now()).slice(-5)}`;
};

export default function Tires({ viewOnly = null }) {
  const { showSnackbar } = useSnackbar();
  const { t, lang } = useI18n();
  const tr = (fr, ar) => (lang === 'ar' ? ar : fr);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const scanInputRef = useRef(null);

  const formatMoney = useCallback(
    (value) =>
      new Intl.NumberFormat(lang === 'ar' ? 'ar-EG' : 'fr-FR', {
        style: 'currency',
        currency: 'EUR',
      }).format(toNumber(value)),
    [lang]
  );

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [partners, setPartners] = useState([]);

  const [mode, setMode] = useState(viewOnly || 'manage');
  const isCashView = mode === 'cash';
  const [search, setSearch] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);

  const [createForm, setCreateForm] = useState({
    name: '', reference: '', category: '', barcode: '', supplier_id: '',
    purchase_price: '', sale_price: '', initial_stock: '',
  });

  const [cashType, setCashType] = useState('NORMAL');
  const [cashSearch, setCashSearch] = useState('');
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [partnerPriceMap, setPartnerPriceMap] = useState({});
  const [discountPercent, setDiscountPercent] = useState(0);
  const [cart, setCart] = useState({});
  const [cartQtyInput, setCartQtyInput] = useState({});
  const [cashProcessing, setCashProcessing] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get('/products');
      setItems(res.data || []);
    } catch (err) {
      showSnackbar(err.response?.data?.message || t('products.fetchFailed'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, t]);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await API.get('/suppliers');
      setSuppliers(res.data || []);
    } catch {
      setSuppliers([]);
    }
  }, []);

  const fetchPartners = useCallback(async () => {
    try {
      const res = await API.get('/partners');
      const rows = res.data || [];
      setPartners(rows);
      if (rows.length > 0) setSelectedPartnerId((prev) => prev || String(rows[0].id));
    } catch {
      setPartners([]);
      setSelectedPartnerId('');
    }
  }, []);

  const fetchPartnerPrices = useCallback(async (partnerId) => {
    if (!partnerId) {
      setPartnerPriceMap({});
      return;
    }
    try {
      const res = await API.get(`/partners/${partnerId}/prices`);
      const map = {};
      (res.data || []).forEach((row) => {
        const id = Number(row.product_id || row.tire_id);
        if (Number.isFinite(id)) map[id] = Number(row.price || 0);
      });
      setPartnerPriceMap(map);
    } catch {
      setPartnerPriceMap({});
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchSuppliers();
    fetchPartners();
  }, [fetchItems, fetchSuppliers, fetchPartners]);

  useEffect(() => {
    if (cashType === 'PARTNER') fetchPartnerPrices(selectedPartnerId);
    else setPartnerPriceMap({});
  }, [cashType, selectedPartnerId, fetchPartnerPrices]);

  useEffect(() => {
    if (mode === 'cash') scanInputRef.current?.focus();
  }, [mode]);

  useEffect(() => {
    if (viewOnly) setMode(viewOnly);
  }, [viewOnly]);

  const selectedPartner = useMemo(
    () => partners.find((p) => String(p.id) === String(selectedPartnerId)) || null,
    [partners, selectedPartnerId]
  );

  const discountFactor = useMemo(
    () => (100 - Math.min(100, Math.max(0, toNumber(discountPercent)))) / 100,
    [discountPercent]
  );

  const getBaseUnitPrice = useCallback((item) => {
    const id = getItemId(item);
    if (cashType === 'PARTNER' && Number.isFinite(partnerPriceMap[id])) {
      return Number(partnerPriceMap[id]);
    }
    return Number(item.sale_price || 0);
  }, [cashType, partnerPriceMap]);

  const categories = useMemo(() => {
    const set = new Set(items.map((x) => String(x.category || '').trim()).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filteredManageItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const text = `${item.name || ''} ${item.reference || ''} ${item.category || ''} ${item.barcode || ''}`.toLowerCase();
      return text.includes(q);
    });
  }, [items, search]);

  const filteredCashItems = useMemo(() => {
    const q = cashSearch.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const text = `${item.name || ''} ${item.reference || ''} ${item.category || ''} ${item.barcode || ''}`.toLowerCase();
      return text.includes(q);
    });
  }, [items, cashSearch]);

  const exactScannedItem = useMemo(() => {
    const q = cashSearch.trim().toLowerCase();
    if (!q) return null;
    return items.find((item) => {
      const ref = String(item.reference || '').toLowerCase();
      const barcode = String(item.barcode || '').toLowerCase();
      return ref === q || barcode === q;
    }) || null;
  }, [items, cashSearch]);
  const cartLines = useMemo(() => Object.entries(cart).map(([id, qty]) => {
    const item = items.find((row) => getItemId(row) === Number(id));
    if (!item) return null;
    const quantity = Math.max(1, Math.floor(toNumber(qty)));
    const baseUnitPrice = getBaseUnitPrice(item);
    const unitPrice = round2(baseUnitPrice * discountFactor);
    return {
      id: Number(id),
      item,
      quantity,
      baseUnitPrice,
      unitPrice,
      lineTotal: round2(unitPrice * quantity),
    };
  }).filter(Boolean), [cart, items, getBaseUnitPrice, discountFactor]);

  const cartSubtotal = useMemo(() => cartLines.reduce((s, l) => s + round2(l.baseUnitPrice * l.quantity), 0), [cartLines]);
  const cartTotal = useMemo(() => cartLines.reduce((s, l) => s + l.lineTotal, 0), [cartLines]);
  const discountAmount = useMemo(() => round2(cartSubtotal - cartTotal), [cartSubtotal, cartTotal]);
  const canPrintReceipt = useMemo(() => Boolean(receiptData) && cartLines.length === 0, [receiptData, cartLines.length]);

  const addToCart = (item) => {
    const id = getItemId(item);
    setCart((prev) => ({ ...prev, [id]: Math.max(1, Math.floor(toNumber(prev[id] || 0) + 1)) }));
  };

  const setCartQty = (itemId, value) => {
    if (value === '' || value == null) return;
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return;
    const qty = Math.floor(parsed);
    if (qty <= 0) {
      setCart((prev) => ({ ...prev, [itemId]: 1 }));
      return;
    }
    setCart((prev) => ({ ...prev, [itemId]: qty }));
  };

  const removeFromCart = (itemId) => {
    setCartQtyInput((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
    setCart((prev) => {
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  };

  const handleCartQtyChange = (itemId, rawValue) => {
    setCartQtyInput((prev) => ({ ...prev, [itemId]: rawValue }));
    if (rawValue === '') return;
    setCartQty(itemId, rawValue);
  };

  const handleCartQtyBlur = (itemId, fallbackQty) => {
    setCartQtyInput((prev) => {
      const raw = prev[itemId];
      const next = { ...prev };
      delete next[itemId];
      if (raw === '' || raw == null) {
        setCart((cartPrev) => ({ ...cartPrev, [itemId]: Math.max(1, Math.floor(toNumber(fallbackQty || 1))) }));
      }
      return next;
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.name.trim()) {
      showSnackbar(t('products.nameRequired'), 'error');
      return;
    }
    const reference = createForm.reference.trim() || buildReference(createForm.name);
    try {
      await API.post('/products', {
        reference,
        name: createForm.name.trim(),
        category: createForm.category.trim() || null,
        barcode: createForm.barcode.trim() || null,
        supplier_id: createForm.supplier_id ? Number(createForm.supplier_id) : null,
        purchase_price: toNumber(createForm.purchase_price),
        sale_price: toNumber(createForm.sale_price),
        initial_stock: Math.max(0, Math.floor(toNumber(createForm.initial_stock))),
      });
      setCreateForm({
        name: '', reference: '', category: createForm.category, barcode: '', supplier_id: '',
        purchase_price: '', sale_price: '', initial_stock: '',
      });
      showSnackbar(t('products.createDone'), 'success');
      fetchItems();
    } catch (err) {
      showSnackbar(err.response?.data?.message || t('products.createFailed'), 'error');
    }
  };

  const printReceipt = (data) => {
    if (!data?.lines || data.lines.length === 0) {
      showSnackbar(tr('Aucun ticket a imprimer.', 'لا يوجد إيصال للطباعة.'), 'error');
      return;
    }
    const bodyRows = data.lines.map((line) => `<tr><td>${line.name}</td><td>${line.reference}</td><td>${line.quantity}</td><td>${formatMoney(line.unitPrice)}</td><td>${formatMoney(line.lineTotal)}</td></tr>`).join('');
    const html = `<html><head><title>${t('pos.receiptTitle')}</title><style>body{font-family:Arial,sans-serif;padding:20px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:8px}th{background:#f5f5f5}.tot{margin-top:12px;max-width:320px;margin-left:auto}.r{display:flex;justify-content:space-between;padding:4px 0}</style></head><body><h2>${t('pos.receiptTitle')}</h2><div>Date: ${data.date}</div><div>${t('pos.receiptType')}: ${data.typeLabel}</div><div>${t('pos.receiptPartner')}: ${data.partnerLabel}</div><div>${t('pos.receiptPayment')}: ${data.paymentLabel}</div><table><thead><tr><th>${t('common.article')}</th><th>${t('common.reference')}</th><th>${t('common.quantity')}</th><th>${t('common.price')}</th><th>${t('pos.lineTotal')}</th></tr></thead><tbody>${bodyRows}</tbody></table><div class='tot'><div class='r'><span>${t('pos.subtotal')}</span><span>${formatMoney(data.subtotal)}</span></div><div class='r'><span>${t('pos.discount')}</span><span>- ${formatMoney(data.discountAmount)}</span></div><div class='r'><b>${t('pos.total')}</b><b>${formatMoney(data.total)}</b></div></div></body></html>`;

    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
      showSnackbar(t('pos.popupBlocked'), 'error');
      return;
    }
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  const checkoutCart = async () => {
    const hasInvalidQtyInput = Object.keys(cart).some((id) => {
      const raw = cartQtyInput[id];
      if (raw === undefined) return false;
      if (raw === '' || raw == null) return true;
      const n = Number(raw);
      return !Number.isFinite(n) || Math.floor(n) < 1;
    });
    if (hasInvalidQtyInput) {
      showSnackbar(tr('Veuillez saisir une quantite valide (min 1).', 'يرجى إدخال كمية صالحة (الحد الأدنى 1).'), 'error');
      return;
    }

    if (cartLines.length === 0) {
      showSnackbar(t('common.emptyCart'), 'error');
      return;
    }
    const totalQuantity = cartLines.reduce((sum, line) => sum + Number(line.quantity || 0), 0);
    if (totalQuantity < 1) {
      showSnackbar(tr("Achat impossible sans quantite d'article >= 1.", 'لا يمكن إتمام الشراء بدون كمية منتجات أكبر أو تساوي 1.'), 'error');
      return;
    }
    if (cashType === 'PARTNER' && !selectedPartnerId) {
      showSnackbar(t('pos.partnerRequired'), 'error');
      return;
    }

    const checkoutLines = cartLines.map((l) => ({
      id: l.id,
      quantity: l.quantity,
      unitPrice: l.unitPrice,
      lineTotal: l.lineTotal,
      name: l.item.name || '-',
      reference: l.item.reference || '-',
    }));
    if (checkoutLines.length === 0) {
      showSnackbar(t('common.emptyCart'), 'error');
      return;
    }

    setCashProcessing(true);
    try {
      for (const line of checkoutLines) {
        if (cashType === 'PARTNER') {
          await API.post('/stock/checkout/partner', {
            product_id: line.id,
            partner_id: Number(selectedPartnerId),
            quantity: line.quantity,
            unit_price_override: line.unitPrice,
          });
        } else {
          await API.post('/stock/checkout', {
            product_id: line.id,
            quantity: line.quantity,
            unit_price_override: line.unitPrice,
          });
        }
      }

      const receipt = {
        date: new Date().toLocaleString(lang === 'ar' ? 'ar-EG' : 'fr-FR'),
        typeLabel: cashType === 'PARTNER' ? t('pos.saleTypePartner') : t('pos.saleTypeNormal'),
        partnerLabel: cashType === 'PARTNER' ? selectedPartner?.name || '-' : '-',
        paymentLabel: cashType === 'PARTNER' && selectedPartner?.type === 'INTERN' ? t('pos.paymentLater') : t('pos.paymentNow'),
        lines: checkoutLines.map((l) => ({ name: l.name, reference: l.reference, quantity: l.quantity, unitPrice: l.unitPrice, lineTotal: l.lineTotal })),
        subtotal: cartSubtotal,
        discountAmount,
        total: cartTotal,
      };
      setReceiptData(receipt);
      showSnackbar(t('pos.checkoutSuccess'), 'success');
      setCart({});
      fetchItems();
      fetchPartners();
      scanInputRef.current?.focus();
    } catch (err) {
      showSnackbar(err.response?.data?.message || t('pos.checkoutFailed'), 'error');
    } finally {
      setCashProcessing(false);
    }
  };

  const cartContent = (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('pos.cartTitle')}</Typography>
      <TableContainer sx={{ maxHeight: { xs: 360, lg: 'calc(100vh - 340px)' } }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>{t('common.article')}</TableCell><TableCell>{t('common.price')}</TableCell><TableCell>{t('common.quantity')}</TableCell><TableCell>{t('pos.lineTotal')}</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {cartLines.map((line) => (
              <TableRow key={line.id}>
                <TableCell sx={{ fontWeight: 600 }}>{line.item.name || line.item.reference}</TableCell>
                <TableCell>{formatMoney(line.unitPrice)}</TableCell>
                <TableCell><TextField type="number" size="small" value={cartQtyInput[line.id] ?? String(line.quantity)} onChange={(e) => handleCartQtyChange(line.id, e.target.value)} onBlur={() => handleCartQtyBlur(line.id, line.quantity)} inputProps={{ min: 1 }} sx={{ width: 90 }} /></TableCell>
                <TableCell>{formatMoney(line.lineTotal)}</TableCell>
                <TableCell><Button size="small" onClick={() => removeFromCart(line.id)}>{t('common.remove')}</Button></TableCell>
              </TableRow>
            ))}
            {cartLines.length === 0 && <TableRow><TableCell colSpan={5}>{t('pos.cartEmpty')}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider sx={{ my: 2 }} />
      <Stack spacing={1}><Stack direction="row" justifyContent="space-between"><Typography>{t('pos.subtotal')}</Typography><Typography>{formatMoney(cartSubtotal)}</Typography></Stack><Stack direction="row" justifyContent="space-between"><Typography>{t('pos.discount')}</Typography><Typography>- {formatMoney(discountAmount)}</Typography></Stack><Stack direction="row" justifyContent="space-between"><Typography sx={{ fontWeight: 700 }}>{t('pos.total')}</Typography><Typography sx={{ fontWeight: 800 }}>{formatMoney(cartTotal)}</Typography></Stack></Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mt={2.5}>
        <Button variant="outlined" onClick={() => setCart({})} disabled={cartLines.length === 0 || cashProcessing}>{t('common.clearCart')}</Button>
        <Button variant="contained" size="large" onClick={checkoutCart} disabled={cartLines.length === 0 || cashProcessing} sx={{ fontWeight: 800 }}>{cashProcessing ? t('common.processing') : t('common.checkoutAll')}</Button>
        <Button variant="text" onClick={() => receiptData && printReceipt(receiptData)} disabled={!canPrintReceipt}>{t('common.printLastReceipt')}</Button>
      </Stack>
    </>
  );
  return (
    <Box
      dir={isCashView ? 'ltr' : undefined}
      sx={{ minHeight: '100vh', p: { xs: 2, md: 4 }, pb: mode === 'cash' && isMobile ? 12 : undefined, background: 'var(--app-bg)' }}
    >
      <Stack spacing={3} sx={{ maxWidth: 1250, mx: 'auto' }}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>{t('products.title')}</Typography>
          <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>{t('products.subtitle')}</Typography>
          {!viewOnly && (
            <Stack direction="row" spacing={1} mt={2.5}>
              <Button variant={mode === 'manage' ? 'contained' : 'outlined'} onClick={() => setMode('manage')}>{t('products.manageTab')}</Button>
              <Button variant={mode === 'cash' ? 'contained' : 'outlined'} onClick={() => setMode('cash')}>{t('products.cashTab')}</Button>
            </Stack>
          )}
        </Paper>

        {mode === 'cash' && (
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
            <Paper sx={{ p: 3, borderRadius: 3, flex: 1.4 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('pos.title')}</Typography>
              <Typography sx={{ color: 'text.secondary', mb: 2 }}>{t('pos.subtitle')}</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mb={2}>
                <TextField select label={t('pos.saleType')} value={cashType} onChange={(e) => setCashType(e.target.value)} sx={{ minWidth: { xs: '100%', md: 180 } }}>
                  <MenuItem value="NORMAL">{t('pos.normal')}</MenuItem>
                  <MenuItem value="PARTNER" disabled={partners.length === 0}>{t('pos.partner')}</MenuItem>
                </TextField>
                {cashType === 'PARTNER' && (
                  <TextField select label={t('pos.partnerLabel')} value={selectedPartnerId} onChange={(e) => setSelectedPartnerId(e.target.value)} sx={{ minWidth: { xs: '100%', md: 260 } }}>
                    {partners.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>{p.name} {p.type === 'INTERN' ? `(${t('pos.partnerLaterPay')})` : ''}</MenuItem>
                    ))}
                  </TextField>
                )}
                <TextField
                  inputRef={scanInputRef}
                  label={t('pos.scanSearch')}
                  value={cashSearch}
                  onChange={(e) => setCashSearch(e.target.value)}
                  placeholder={t('pos.scanPlaceholder')}
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const enterMatch = exactScannedItem || (filteredCashItems.length === 1 ? filteredCashItems[0] : null);
                      if (enterMatch) {
                        addToCart(enterMatch);
                        setCashSearch('');
                        requestAnimationFrame(() => scanInputRef.current?.focus());
                      }
                    }
                  }}
                />
                <TextField label={t('pos.globalDiscount')} type="number" value={discountPercent} onChange={(e) => setDiscountPercent(Math.min(100, Math.max(0, toNumber(e.target.value))))} inputProps={{ min: 0, max: 100, step: 0.1 }} sx={{ minWidth: { xs: '100%', md: 170 } }} />
              </Stack>

              {cashType === 'PARTNER' && selectedPartner?.type === 'INTERN' && (
                <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}><Typography sx={{ fontWeight: 700 }}>{t('pos.partnerCreditHint')}</Typography></Paper>
              )}

              {loading ? (
                <Stack alignItems="center" py={4}><CircularProgress /></Stack>
              ) : isMobile ? (
                <Stack spacing={1.5}>
                  {filteredCashItems.map((item) => {
                    const id = getItemId(item);
                    const price = round2(getBaseUnitPrice(item) * discountFactor);
                    const outOfStock = Number(item.stock || 0) <= 0;
                    return (
                      <Paper key={id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Stack spacing={1.1}>
                          <Typography sx={{ fontWeight: 700 }}>{item.name || '-'}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('common.reference')}: {item.reference || '-'}
                          </Typography>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600 }}>
                              {t('common.price')}: {formatMoney(price)}
                            </Typography>
                            <Chip label={`${t('common.stock')}: ${Number(item.stock || 0)}`} size="small" color={outOfStock ? 'error' : 'success'} />
                          </Stack>
                          <Button variant="contained" size="small" onClick={() => addToCart(item)} disabled={outOfStock}>
                            {t('pos.addToCart')}
                          </Button>
                        </Stack>
                      </Paper>
                    );
                  })}
                  {filteredCashItems.length === 0 && <Typography>{t('products.empty')}</Typography>}
                </Stack>
              ) : (
                <TableContainer sx={{ maxHeight: 520 }}>
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow><TableCell>{t('common.article')}</TableCell><TableCell>{t('common.reference')}</TableCell><TableCell>{t('common.price')}</TableCell><TableCell>{t('common.stock')}</TableCell><TableCell>{t('common.action')}</TableCell></TableRow></TableHead>
                    <TableBody>
                      {filteredCashItems.map((item) => {
                        const id = getItemId(item);
                        const price = round2(getBaseUnitPrice(item) * discountFactor);
                        return (
                          <TableRow key={id} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{item.name || '-'}</TableCell>
                            <TableCell>{item.reference || '-'}</TableCell>
                            <TableCell>{formatMoney(price)}</TableCell>
                            <TableCell><Chip label={Number(item.stock || 0)} size="small" color={Number(item.stock || 0) > 0 ? 'success' : 'error'} /></TableCell>
                            <TableCell><Button variant="contained" size="small" onClick={() => addToCart(item)} disabled={Number(item.stock || 0) <= 0}>{t('pos.addToCart')}</Button></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {!isMobile && (
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  flex: 1,
                  alignSelf: 'flex-start',
                  position: { xs: 'static', lg: 'sticky' },
                  top: { lg: 16 },
                }}
              >
                {cartContent}
              </Paper>
            )}
          </Stack>
        )}

        {mode === 'cash' && isMobile && (
          <>
            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                left: 12,
                right: 12,
                bottom: 12,
                zIndex: 1200,
                p: 1.2,
                borderRadius: 2.5,
                border: '1px solid var(--border-subtle)',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography sx={{ fontWeight: 700 }}>{cartLines.length} {t('common.article')}</Typography>
                <Typography sx={{ fontWeight: 800 }}>{formatMoney(cartTotal)}</Typography>
                <Button variant="contained" onClick={() => setCartDrawerOpen(true)}>
                  {t('pos.cartTitle')}
                </Button>
              </Stack>
            </Paper>
            <Drawer
              anchor="bottom"
              open={cartDrawerOpen}
              onClose={() => setCartDrawerOpen(false)}
              PaperProps={{ sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '85vh', p: 2 } }}
            >
              {cartContent}
            </Drawer>
          </>
        )}
        {mode === 'manage' && (
          <>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="contained" onClick={() => setShowCreateForm((prev) => !prev)}>
                  {showCreateForm ? t('snackbar.cancel') : t('products.newProduct')}
                </Button>
              </Stack>
            </Paper>

            {showCreateForm && (
              <Paper component="form" onSubmit={handleCreate} sx={{ p: 3, borderRadius: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('products.newProduct')}</Typography>
                  <Button size="small" variant="text" onClick={() => setShowOptionalFields((v) => !v)}>
                    {showOptionalFields
                      ? tr('Masquer les champs optionnels', 'إخفاء الحقول الاختيارية')
                      : tr('Afficher les champs optionnels', 'إظهار الحقول الاختيارية')}
                  </Button>
                </Stack>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField label={`${t('products.name')} *`} value={createForm.name} onChange={(e) => setCreateForm((s) => ({ ...s, name: e.target.value }))} fullWidth required />
                    <TextField label={t('products.salePrice')} type="number" value={createForm.sale_price} onChange={(e) => setCreateForm((s) => ({ ...s, sale_price: e.target.value }))} fullWidth />
                    <TextField label={t('products.initialStock')} type="number" value={createForm.initial_stock} onChange={(e) => setCreateForm((s) => ({ ...s, initial_stock: e.target.value }))} fullWidth />
                  </Stack>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <Autocomplete
                      freeSolo
                      options={categories}
                      value={createForm.category}
                      onInputChange={(_, value) => setCreateForm((s) => ({ ...s, category: value || '' }))}
                      renderInput={(params) => <TextField {...params} label={t('products.category')} helperText={t('products.categoryHint')} />}
                      fullWidth
                    />
                  </Stack>
                  {showOptionalFields && (
                    <>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField label={t('products.referenceOptional')} value={createForm.reference} onChange={(e) => setCreateForm((s) => ({ ...s, reference: e.target.value }))} fullWidth helperText={t('products.autoReferenceHint')} />
                        <TextField label={t('products.barcode')} value={createForm.barcode} onChange={(e) => setCreateForm((s) => ({ ...s, barcode: e.target.value }))} fullWidth />
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField select label={t('products.supplierOptional')} value={createForm.supplier_id} onChange={(e) => setCreateForm((s) => ({ ...s, supplier_id: e.target.value }))} fullWidth>
                          <MenuItem value="">{t('products.noSupplier')}</MenuItem>
                          {suppliers.map((supplier) => <MenuItem key={supplier.id} value={String(supplier.id)}>{supplier.name}</MenuItem>)}
                        </TextField>
                        <TextField label={t('products.purchasePrice')} type="number" value={createForm.purchase_price} onChange={(e) => setCreateForm((s) => ({ ...s, purchase_price: e.target.value }))} fullWidth />
                      </Stack>
                    </>
                  )}
                  <Box><Button type="submit" variant="contained" sx={{ minWidth: 180 }}>{t('products.create')}</Button></Box>
                </Stack>
              </Paper>
            )}
            
            {/* end create form */}

            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>{t('products.listTitle')}</Typography>
                <TextField label={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('products.searchPlaceholder')} sx={{ minWidth: { xs: '100%', md: 360 } }} />
              </Stack>
              {loading ? <Stack alignItems="center" py={4}><CircularProgress /></Stack> : isMobile ? (
                <Stack spacing={1.5}>
                  {filteredManageItems.map((item) => {
                    const id = getItemId(item);
                    return (
                      <Paper key={id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                        <Stack spacing={1.2}>
                          <Typography sx={{ fontWeight: 700 }}>{item.name || '-'}</Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('products.reference')}: {item.reference || '-'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('common.category')}: {item.category || '-'}
                          </Typography>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography sx={{ fontWeight: 600 }}>
                              {t('common.price')}: {formatMoney(item.sale_price)}
                            </Typography>
                            <Chip label={`${t('common.stock')}: ${Number(item.stock || 0)}`} size="small" color={Number(item.stock || 0) > 0 ? 'success' : 'error'} />
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                  {filteredManageItems.length === 0 && <Typography>{t('products.empty')}</Typography>}
                </Stack>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>{t('products.name')}</TableCell><TableCell>{t('products.reference')}</TableCell><TableCell>{t('common.category')}</TableCell><TableCell>{t('common.price')}</TableCell><TableCell>{t('common.stock')}</TableCell></TableRow></TableHead>
                    <TableBody>
                      {filteredManageItems.map((item) => {
                        return (
                          <TableRow key={getItemId(item)} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{item.name || '-'}</TableCell>
                            <TableCell>{item.reference || '-'}</TableCell>
                            <TableCell>{item.category || '-'}</TableCell>
                            <TableCell>{formatMoney(item.sale_price)}</TableCell>
                            <TableCell><Chip label={Number(item.stock || 0)} size="small" color={Number(item.stock || 0) > 0 ? 'success' : 'error'} /></TableCell>
                          </TableRow>
                        );
                      })}
                      {filteredManageItems.length === 0 && <TableRow><TableCell colSpan={5}>{t('products.empty')}</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Stack>
    </Box>
  );
}
